import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Body2 } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useAssigneeTitle } from "~/components/AssigneePicker/useAssigneeTitle";
import { useSecurity } from "~/components/Hooks";
import { Date as TaskDatePicker } from "~/components/ListView/columns";
import { ProjectMembersAssigneePicker } from "~/components/ProjectMembersAssigneePicker";
import TaskCompleteCheckbox from "~/components/tasks/TaskCompleteCheckbox";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { TooltipCss } from "~/components/TooltipCss/TooltipCss";
import AvatarContainer from "~/components/Users/AvatarContainer";
import { COMPLETE_TASK_ERROR, COMPLETE_TASK_SUCCESS } from "~/store/action_types";
import { editTask } from "~/store/actions/actions.tasks";
import { getConfigOption } from "~/store/selectors/selector.configOptions";
import { getTaskById, makeGetOpenParentDependenciesCountByIdSelector } from "~/store/selectors/selector.tasks";
import { getUserById, hasLoggedUserElevatedPermissions } from "~/store/selectors/selector.users";

export const CalendarTaskItem = ({ id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const task = useSelector((state) => getTaskById(state, id));
  const showTaskNumber = useSelector((state) => getConfigOption(state, `show_task_id`));
  const getOpenParentDependenciesCountById = useMemo(makeGetOpenParentDependenciesCountByIdSelector, []);
  const parentDependenciesCount = useSelector((state) => getOpenParentDependenciesCountById(state, id));
  const assignee = useSelector((state) => getUserById(state, task?.assignee_id));
  const assigneeTitle = useAssigneeTitle(assignee?.id, task?.fake_assignee_name);
  const can = useSecurity();
  const canModify = can(["edit"], task);
  const canCompleteTask = useSelector(hasLoggedUserElevatedPermissions);

  const handleLink = useCallback(() => {
    navigate(`?modal=Task-${id}-${task?.project_id}`);
  }, [id, navigate, task]);

  const handleChangeAssignee = useCallback(
    async (assigneeId) => {
      const { error } = await dispatch(editTask(task?.project_id, id, { assignee_id: assigneeId }));

      if (error) {
        showErrorToast(`${t("Unable to update task")}.`);
      }
    },
    [dispatch, id, task, t]
  );

  const handleCompleteAction = useCallback(
    (action) => {
      if (action && action.type === COMPLETE_TASK_SUCCESS) {
        showSuccessToast(t("Task completed."));
      } else if (action && action.type === COMPLETE_TASK_ERROR) {
        showErrorToast("Unable to complete task.");
      }
    },
    [t]
  );

  return (
    <div
      className={classNames(
        "tw-w-full tw-max-w-full tw-flex tw-h-10 tw-items-center tw-cursor-pointer tw-border-b tw-border-solid tw-border-0 tw-border-[var(--color-theme-300)] tw-justify-between task-list-item hover:tw-opacity-100 "
      )}
      onClick={handleLink}
      data-testid="calendar-item"
    >
      <TaskCompleteCheckbox
        className="tw-mr-2 tw-ml-1 tw-shrink-0"
        taskId={id}
        defaultCompleted={task?.is_completed}
        parentDependenciesCount={parentDependenciesCount}
        openSubtasksCount={task?.open_subtasks}
        disabled={!canCompleteTask}
        data-testid="CompleteCheckbox"
        onComplete={handleCompleteAction}
      />
      <Body2
        weight="medium"
        className={classNames("tw-truncate tw-flex-1", {
          "tw-text-red-alert": task?.is_important,
        })}
      >
        {showTaskNumber ? `#${task?.task_number}: ${task?.name}` : `${task?.name}`}
      </Body2>

      <TaskDatePicker
        taskId={task?.get("id")}
        projectId={task?.get("project_id")}
        className="tw-hidden md:tw-flex"
      />
      {!canModify ? (
        assignee?.id > 0 ? (
          <TooltipCss
            position="left"
            title={assigneeTitle}
            className="tw-flex tw-items-center"
          >
            <AvatarContainer
              id={assignee?.id}
              className="tw-px-1"
            />
          </TooltipCss>
        ) : (
          <div className="tw-w-6 tw-ml-1"></div>
        )
      ) : (
        <ProjectMembersAssigneePicker
          projectId={task?.project_id}
          assigneeId={assignee?.id}
          fakeAssigneeName={task?.fake_assignee_name}
          onChange={handleChangeAssignee}
          hiddenFromClient={task?.is_hidden_from_clients}
          className={classNames({
            "show-on-hover": !assignee?.id,
          })}
          position="bottom-end"
          data-testid="ProjectMembersAssigneePicker"
        />
      )}
    </div>
  );
};

CalendarTaskItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
